<script setup lang="ts">
import { PropType } from 'vue'
import Product from '~/models/Product/Product'
import ImagePreview from '~/components/elements/ImagePreview.vue'
import ProductFavoriteButton from '~/components/objects/ProductFavoriteButton.vue'
import { useBasketStore } from '~/stores/basket'

defineProps({
    product: {
        type: Object as PropType<Product>,
        required: true,
    },
    lazyImage: {
        type: Boolean,
        default: true,
    },
    isCarousel: {
        type: Boolean,
        default: false,
    },
    small: {
        type: Boolean,
        default: false,
    },
    showRating: {
        type: Boolean,
        default: true,
    },
    showRatingPopUp: {
        type: Boolean,
        default: true,
    },
})

const authUser = computed(() => {
    const basketStore = useBasketStore()
    return basketStore.authUser
})

const ratingShowStar = 3

const emit = defineEmits(['redirectToUrl'])
</script>

<template>
    <div
        :class="`product__image js-product-stats js-product-stats-${product.id} ${small ? 'product__image--small' : ''}`"
        :data-id="product.id"
    >
        <ProductFavoriteButton v-show="authUser" :product="(product as Product)" />

        <a :href="product.url" class="img-container" :class="{ small }" @click.prevent="emit('redirectToUrl', $event)">
            <ImagePreview
                :image-url="product.imageCdn"
                :image-alt="product.name"
                :lazy-image="lazyImage"
                :out-of-stock-greyed-out="product.attributes?.outOfStockGreyedOut || !product.isAvailable"
                :image-by-size="product.imageBySize || null"
            />

            <ObjectsProductBadges v-if="!small" :product="product" />

            <ObjectsRating
                v-if="showRating && product.productRating && product.productRating?.averageRating > ratingShowStar"
                class="productRating my-1"
                :product-id="product.id"
                :product-url="product.url"
                :rating="product.productRating.averageRating"
                :total="product.productRating.totalReviews"
                :clickable="false"
                :show-popup="true"
                :box="true"
                :more-info="showRatingPopUp"
                :is-carousel="isCarousel"
                :product-rating="product.productRating"
            />
        </a>
    </div>
</template>

<style scoped lang="scss">
.product {
    &__image {
        position: relative;
        margin-bottom: 25px;

        &--small {
            margin-bottom: 0;
        }

        img {
            max-width: 100%;
            max-height: 100%;
            height: auto;
            width: auto;
        }
    }
}

.img-container {
    position: relative;
    height: 120px;
    display: block;
    padding-top: 50%;

    @include screen-sm() {
        height: 160px;
    }

    &.small {
        height: 50px;

        @include screen-sm() {
            height: 100px;
        }
    }
}

.productRating {
    justify-content: center;
    position: absolute;
    left: 0;
    bottom: -67px;
    right: 0;
    z-index: 1;

    &:deep(> div) {
        display: inline-block;
    }
}
</style>
